import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '../../api/client';
import { parseJobTicketOptions, ticketOptionLabel, type JobTicketOption } from '../../lib/expenses';

/**
 * The tickets on one job, for the expense ticket picker.
 *
 * Keyed on the job, so two rows of the office approval list filed against one job
 * share one request rather than each firing their own.
 */
export const jobTicketsQueryKey = (jobId: string | null) => ['job-tickets', jobId ?? ''] as const;

export function useJobTickets(jobId: string | null) {
  return useQuery({
    queryKey: jobTicketsQueryKey(jobId),
    // The param is camelCase-only on GET /tickets. `job_id` is ignored there,
    // and an ignored filter returns EVERY open ticket in the tenant — the
    // wrong-job link the API's 400 exists to stop, offered by our own picker.
    queryFn: () => api.tickets.list({ jobId: jobId as string }).then((r) => parseJobTicketOptions(r.data)),
    enabled: !!jobId,
    staleTime: 30_000,
  });
}

interface Props {
  /** The expense's job. Nothing is fetched and nothing renders until there is one. */
  jobId: string | null;
  /** The chosen ticket id, or null for none. Controlled. */
  value: string | null;
  onChange: (ticketId: string | null) => void;
  id?: string;
  /**
   * An accessible name for callers with no `<label htmlFor>` — the office row
   * has two selects in one cell-pair now (this and "Who paid"), and an
   * unnamed one is indistinguishable from its neighbour to a screen reader
   * and to any query that asks for "the select".
   */
  ariaLabel?: string;
  disabled?: boolean;
  /** Dark-field vs office-theme classes are the caller's; the behaviour is not. */
  selectClassName: string;
  noteClassName: string;
}

/**
 * "Ticket (optional)" — which ticket a receipt was incurred on.
 *
 * OPTIONAL, AND NEVER A GATE. A receipt is photographed before the day's
 * ticket exists, so a job with no tickets yet is the ordinary case and not a
 * degraded one: it says so in a sentence and the form submits with the ticket
 * blank. A failed lookup does the same — a read that could not answer must not
 * turn into a write the hand cannot complete.
 *
 * EXACTLY ONE TICKET PRESELECTS IT, ONCE. The common shape is one ticket on
 * the job, and making the hand pick the only option is a step that can only be
 * got wrong. It is preselected only while nothing is chosen and only once per
 * job — a person who deliberately picks "No ticket" is not overruled on the
 * next render, which is what an unguarded effect would do.
 *
 * The options are the tickets on THIS job. `tickets.job_id` is NOT NULL, so a
 * ticket names exactly one job and the API refuses a ticket from another with
 * a 400 naming both; offering one would be offering an error.
 */
export default function TicketSelect({ jobId, value, onChange, id, ariaLabel, disabled, selectClassName, noteClassName }: Props) {
  const { data, isLoading, isError, error } = useJobTickets(jobId);
  // Memoised because it is an effect dependency: `data ?? []` would mint a new
  // empty array every render and re-run the preselect on every one of them.
  const options: JobTicketOption[] = useMemo(() => data ?? [], [data]);

  // Which job we have already auto-chosen for. A job with no ticket yet, then
  // one, still preselects — the ref only stops the SAME resolved job from
  // re-choosing after the user cleared it.
  const autoChosenFor = useRef<string | null>(null);
  useEffect(() => {
    if (!jobId || isLoading || isError) return;
    if (autoChosenFor.current === jobId) return;
    if (options.length !== 1) return;
    autoChosenFor.current = jobId;
    if (value === null) onChange(options[0].id);
  }, [jobId, isLoading, isError, options, value, onChange]);

  if (!jobId) return null;

  if (isLoading) {
    return <p className={noteClassName}>Loading tickets for this job…</p>;
  }
  if (isError) {
    // Not an error state for the form. The ticket is optional; say what failed
    // and let the receipt be filed without one.
    return (
      <p className={noteClassName}>
        {getApiErrorMessage(error, "Couldn't load this job's tickets.")} You can file this without a ticket.
      </p>
    );
  }
  if (options.length === 0) {
    return <p className={noteClassName}>No tickets on this job yet</p>;
  }

  // `value` may name a ticket that is not in the list — one that was archived
  // or soft-deleted since the link was made. Keep it selectable rather than
  // letting the select silently show the first option instead.
  const missing = value !== null && !options.some((t) => t.id === value);

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      className={selectClassName}
    >
      <option value="">No ticket</option>
      {missing && <option value={value as string}>{`Ticket ${(value as string).slice(0, 8)}`}</option>}
      {options.map((t) => (
        <option key={t.id} value={t.id}>{ticketOptionLabel(t)}</option>
      ))}
    </select>
  );
}
